import { type StructuredText as StructuredTextType } from 'datocms-structured-text-utils'
import { graphql, type HeadProps, type PageProps } from 'gatsby'
import React, { useEffect, useRef } from 'react'

import Newsletter from '@/components/centered-call-to-action/centered-call-to-action'
import HeaderBackgroundV3 from '@/components/header/header-background-v3'
import Image from '@/components/image/image'
import Link from '@/components/link/link'
import Headline from '@/components/models/post/headline-post/headline-post'
import Related from '@/components/related/related'
import { useSiteMetadata } from '@/components/root-element'
import { Microdata } from '@/components/seo/microdata'
import Seo from '@/components/seo/seo'
import StructuredTextDefault from '@/components/structured-text/structured-text-default'
import Tags from '@/components/tags/tags'
import Layout from '@/layouts/layout'
import { type CountObject } from '@/types/datocms-plugin-word-counter'
import { resolveImageAttribution } from '@/utils/image-author'
import { toReactNode } from '@/utils/react'
import { sortRelatedContent } from '@/utils/sort'
import { generateUrlFromNode } from '@/utils/url'

import * as styles from './post.module.scss'

export type DataProps = {
  datoCmsPctPost: Queries.PostQuery['datoCmsPctPost']
  allDatoCmsPctPost: Queries.PostQuery['allDatoCmsPctPost']
  allDatoCmsPctInterview: Queries.PostQuery['allDatoCmsPctInterview']
  allDatoCmsPctPaper: Queries.PostQuery['allDatoCmsPctPaper']
  allDatoCmsPctDialog: Queries.PostQuery['allDatoCmsPctDialog']
  allDatoCmsPctProject: Queries.PostQuery['allDatoCmsPctProject']
  allDatoCmsPctEpisode: Queries.PostQuery['allDatoCmsPctEpisode']
  allDatoCmsPctPage: Queries.PostQuery['allDatoCmsPctPage']
  allDatoCmsPctExternalContent: Queries.PostQuery['allDatoCmsPctExternalContent']
  allDatoCmsPctEvent: Queries.PostQuery['allDatoCmsPctEvent']
}
export const Head: React.FC<HeadProps<DataProps>> = ({
  data: { datoCmsPctPost },
  location
}) => {
  return (
    <>
      <Seo location={location} data={datoCmsPctPost} />
      <Microdata data={datoCmsPctPost} location={location} />
    </>
  )
}

const Post: React.FC<PageProps<DataProps>> = ({
  data: {
    datoCmsPctPost,
    allDatoCmsPctPost,
    allDatoCmsPctInterview,
    allDatoCmsPctPaper,
    allDatoCmsPctDialog,
    allDatoCmsPctProject,
    allDatoCmsPctEpisode,
    allDatoCmsPctPage,
    allDatoCmsPctExternalContent,
    allDatoCmsPctEvent
  },
  location
}) => {
  const { settings } = useSiteMetadata()
  const contentBlockRef = useRef<HTMLDivElement>(null)
  const topicBoxRef = useRef<HTMLDivElement>(null)
  const pbRef = useRef<HTMLDivElement>(null)
  const boxFixedRef = useRef<HTMLDivElement>(null)
  const bgRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateStyle = (
      ref: React.RefObject<HTMLDivElement>,
      styles: Partial<CSSStyleDeclaration>
    ): void => {
      if (ref.current) {
        Object.assign(ref.current.style, styles)
      }
    }

    const scrollHandler = (): void => {
      if (contentBlockRef.current) {
        const scrollTop = window.scrollY - 500
        const docHeight = contentBlockRef.current.offsetHeight
        const scrollPercent = scrollTop / docHeight
        const scrollPercentRounded = Math.round(scrollPercent * 100)
        const degrees = scrollPercent * 360
        const offsetTop = topicBoxRef.current?.offsetTop
        const startFixed = offsetTop !== undefined ? offsetTop + 100 : 0

        if (window.scrollY > startFixed) {
          updateStyle(pbRef, { display: 'block' })
          updateStyle(boxFixedRef, { position: 'fixed' })
        } else {
          updateStyle(pbRef, { display: 'none' })
          updateStyle(boxFixedRef, { position: 'static' })
        }

        if (window.scrollY > docHeight + 1000) {
          updateStyle(boxFixedRef, { display: 'none' })
        } else {
          updateStyle(boxFixedRef, { display: 'block' })
        }

        updateStyle(bgRef, {
          background: `conic-gradient(#0072bc ${degrees}deg, #ddd ${degrees}deg)`
        })
        updateStyle(pbRef, {
          background: `linear-gradient(to right, #0072bc ${scrollPercentRounded}%, #eee ${scrollPercentRounded}%)`
        })
      }
    }

    window.addEventListener('scroll', scrollHandler)

    return () => {
      window.removeEventListener('scroll', scrollHandler)
    }
  }, [])

  if (!datoCmsPctPost) return <></>

  const related = sortRelatedContent({
    results: 3,
    nodesGroups: [
      allDatoCmsPctPost.nodes as Card[],
      [
        ...(allDatoCmsPctInterview.nodes ?? []),
        ...(allDatoCmsPctProject.nodes ?? []),
        ...(allDatoCmsPctPaper.nodes ?? []),
        ...(allDatoCmsPctDialog.nodes ?? []),
        ...(allDatoCmsPctEpisode.nodes ?? []),
        ...(allDatoCmsPctPage.nodes ?? []),
        ...(allDatoCmsPctExternalContent.nodes ?? []),
        ...(allDatoCmsPctEvent.nodes ?? [])
      ]
    ]
  })

  const { externalName, topic, image, content, tags, headline, authors, meta } =
    datoCmsPctPost
  const wordCounter = JSON.parse(
    datoCmsPctPost.contentWordCounter as unknown as string
  ) as CountObject
  const { author, customData } = image ?? {}
  const attribution = resolveImageAttribution(author, customData)
  const {
    'title-highlight': titleHighlight,
    'title-related-content': titleRelatedContent,
    'title-authors': titleAuthors,
    'title-author': titleAuthor,
    'title-published-on': titlePublishedOn
  } = settings
  return (
    <Layout location={location}>
      <div>
        <div ref={pbRef} className={`${styles.pb} pb`} />
        <HeaderBackgroundV3 title={externalName} topic={topic} image={image} />
        <div className="container">
          <div className="row justify-content-between">
            <div className={`mt-4 mt-lg-0 ${styles.colContent}`}>
              <Headline
                label={`${titleHighlight as string} /`}
                headline={headline}
              />
              <div
                ref={contentBlockRef}
                className={`${styles.content} content-block`}
              >
                <StructuredTextDefault
                  content={content as unknown as StructuredTextType}
                />
              </div>
              {attribution && (
                <p className="figure-caption">Header photo by {attribution}</p>
              )}
              <Tags tags={tags} />
              <div className={styles.relatedPosts}>
                <Related
                  related={related}
                  title={titleRelatedContent as string}
                />
              </div>
            </div>
            {/* @TODO: Move authors list to component */}
            <div
              className={`${styles.colInfo} ${
                authors && authors.length > 1 ? styles.multipleAuthor : ''
              }`}
            >
              {authors && authors.length > 1 && (
                <div className={`${styles.multiAuthorMobile} container`}>
                  <div className={styles.label}>{titleAuthors as string}</div>
                  <div>
                    {authors
                      .map((author) =>
                        author
                          ? `${author.person?.firstName} ${author.person?.secondName}`
                          : null
                      )
                      .join(', ')}
                  </div>
                </div>
              )}
              {authors &&
                authors.length > 0 &&
                authors.map((author) => {
                  if (!author) return null
                  const url =
                    author.person?.disableProfilePage === true
                      ? ''
                      : generateUrlFromNode(author.person)
                  return (
                    <div className={styles.author} key={author.id}>
                      <Link to={url}>
                        <Image
                          image={author.person?.photoSquare?.gatsbyImageData}
                          alt={`${author.person?.firstName} ${author.person?.secondName}`}
                        />
                      </Link>
                      <div className={styles.infoAuthor}>
                        <div className={styles.label}>
                          {toReactNode(titleAuthor)}
                        </div>
                        <div className={styles.name}>
                          <Link to={url}>
                            {author.person?.firstName}{' '}
                            {author.person?.secondName}
                          </Link>
                        </div>
                        <div className={styles.position}>
                          {author.person?.job}
                        </div>
                      </div>
                    </div>
                  )
                })}

              {meta.firstPublishedAt && (
                <div className={styles.date}>
                  {titlePublishedOn as string} {meta.firstPublishedAt}
                </div>
              )}
              <div className={styles.time}>{wordCounter.readingTime}</div>
              {topic && (
                <div ref={topicBoxRef} className={`topicBox ${styles.topicBd}`}>
                  <Link to={generateUrlFromNode(topic)}>
                    {topic?.externalName}
                  </Link>
                </div>
              )}

              <div ref={boxFixedRef} className={`${styles.boxFixed} box-fixed`}>
                <div className="pc">
                  <div className={`${styles.ol} ol`} />
                  <div ref={bgRef} className={`${styles.bg} bg`} />
                </div>
                <div className={styles.authorsFixed}>
                  {authors && authors.length > 1
                    ? (titleAuthors as string)
                    : (titleAuthor as string)}
                  : <br />
                  {authors?.map((author) => {
                    if (!author) return null
                    const url =
                      author.person?.disableProfilePage === true
                        ? ''
                        : generateUrlFromNode(author.person)
                    return (
                      <div key={author.id}>
                        <Link to={url}>
                          {author.person?.firstName} {author.person?.secondName}
                        </Link>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Newsletter />
      </div>
    </Layout>
  )
}

export default Post

export const query = graphql`
  query Post($id: String, $related: [String] = "") {
    datoCmsPctPost(id: { eq: $id }) {
      ...PostDetail
    }
    allDatoCmsPctPost(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPostCard
      }
    }
    allDatoCmsPctInterview(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctInterviewCard
      }
    }
    allDatoCmsPctPaper(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPaperCard
      }
    }
    allDatoCmsPctPage(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPageCard
      }
    }
    allDatoCmsPctDialog(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctDialogCard
      }
    }
    allDatoCmsPctProject(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctProjectCard
      }
    }
    allDatoCmsPctEpisode(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctEpisodeCard
      }
    }

    allDatoCmsPctExternalContent(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related }, showInWebsite: { eq: true } }
    ) {
      nodes {
        ...PctExternalContentCard
      }
    }
    allDatoCmsPctEvent(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctEventCard
      }
    }
  }

  fragment PostDetail on DatoCmsPctPost {
    __typename
    originalId
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
    seo {
      title
      description
      image {
        url
      }
    }
    id
    meta {
      firstPublishedAt(formatString: "DD/MM/YYYY")
      publishedAt(formatString: "DD/MM/YYYY")
      publishedAtTimestamp: publishedAt(formatString: "X")
    }
    externalName
    slug
    headline
    excerpt
    image {
      gatsbyImageData(
        layout: FULL_WIDTH
        imgixParams: { ar: "16:6", fit: "crop", crop: "focalpoint" }
      )
      alt
      author
      customData
      title
    }
    card: image {
      ...imgCard
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    authors {
      ...Author
    }
    contentWordCounter
    content {
      value
      links {
        __typename
        ... on DatoCmsPctDialog {
          ...PctDialogCardInline
        }
        ... on DatoCmsPctTopic {
          ...PctTopicCardInline
        }
        ... on DatoCmsPctPaper {
          ...PctPaperCardInline
        }
        ... on DatoCmsPctInternalPage {
          ...PctInternalPageCardInline
        }
        ... on DatoCmsPctPost {
          ...PctPostCardInline
        }
        ... on DatoCmsPctInterview {
          ...PctInterviewCardInline
        }
        ... on DatoCmsPctProject {
          ...PctProjectCardInline
        }
        ... on DatoCmsPctPage {
          ...PctPageCardInline
        }
        ... on DatoCmsPctVision {
          ...PctVisionCardInline
        }
        ... on DatoCmsPctTag {
          ...PctTagCardInline
        }
        ... on DatoCmsPctReport {
          ...PctReportCardInline
        }
        ... on DatoCmsPctEpisode {
          ...PctEpisodeCardInline
        }
        ... on DatoCmsPctLink {
          ...PctLink
        }
      }
      blocks {
        __typename
        ... on DatoCmsImage {
          ...ImageInline
        }
        ... on DatoCmsVideo {
          ...VideoInline
        }
        ... on DatoCmsEmbed {
          ...EmbedInline
        }
        ... on DatoCmsForm {
          ...FormInline
        }
      }
    }
  }
`
